/* eslint-disable consistent-return */
import useCommon from '@utils/hooks/common';
import {Button, Form, Slider, Statistic, Row, Col, DrawerProps, InputNumber, Modal} from 'antd';
import classNames from 'classnames';
import Icon, {LoadingOutlined} from '@ant-design/icons';
import React, {useEffect, useState} from 'react';
import {Currency} from '@interface/index';
import {CURRENCIES} from '@constant/system';
import GoldenDIvider from '@components/GoldenDivider';
import styles from './styles';
import dayjs from 'dayjs';
import BigNumber from 'bignumber.js';
import {GameLogicInterface} from 'poker-interfaces';
import CustomModalSheet from '@components/CustomModalSheet';
import GradientButton from '@components/GradientButton';
import PopupContainer from '@components/layout/PopupContainer';

const {Countdown} = Statistic;
interface BuyInDrawerProps extends DrawerProps {
  isBuyInLoading: boolean;
  gameMode: GameLogicInterface.GameType;
  isAllIn: boolean;
  currencyId: Currency;
  expiredAt: string;
  smallBlind?: number;
  bigBlind?: number;
  closeDrawer?: () => void;
  balance: number;
  buyInMinAmount?: number;
  buyInMaxAmount?: number;
  onFinishCallback: ({base}: {base: number}) => Promise<any>;
  onCancelCallback?: () => void;
}

const BuyInModal = (props: BuyInDrawerProps) => {
  const {
    isBuyInLoading,
    gameMode,
    isAllIn,
    smallBlind,
    bigBlind,
    closeDrawer,
    buyInMinAmount,
    buyInMaxAmount,
    onFinishCallback,
    onCancelCallback,
    open: visible,
    balance,
    currencyId,
    expiredAt,
  } = props;

  const {classes, user, t} = useCommon({
    withStyles: styles,
    observers: ['socket', 'user'],
    withTranslation: ['game_room'],
  });

  const [form] = Form.useForm();
  const FIELDS = {
    BASE_AMOUNT: 'baseAmount',
  };
  const stepUnit = smallBlind;

  const [isBalanceEnough, setIsBalanceEnough] = useState<boolean>(false);
  const [buyInValue, setBuyInValue] = useState<number>(smallBlind);

  const sameMinMaxTriggered = true ? buyInMinAmount === buyInMaxAmount : false;

  useEffect(() => {
    if (!visible) return;
    let balance;

    if (currencyId === CURRENCIES.COIN) {
      balance = user.getCoinWalletBalance();
    } else if (currencyId === CURRENCIES.DIAMOND) {
      balance = user.getDiamondWalletBalance();
    } else if (currencyId === CURRENCIES.DEMO_COIN) {
      balance = user.getDemoCoinWalletBalance();
    }
  }, [visible]);

  useEffect(() => {
    if (balance < buyInMinAmount) {
      setIsBalanceEnough(false);
    } else {
      setIsBalanceEnough(true);
    }
  }, [balance, buyInMinAmount]);

  useEffect(() => {
    form.setFieldsValue({
      baseAmount: buyInMinAmount,
    });
    setBuyInValue(buyInMinAmount);
  }, [smallBlind, bigBlind, buyInMinAmount, buyInMaxAmount, balance]);

  const debounceFunction = (fn: any, delay: number) => {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn.apply(this);
      }, delay);
    };
  };

  const handleSliderOnChange = () => {
    const formValue = form.getFieldValue(FIELDS.BASE_AMOUNT);
    setBuyInValue(formValue);
  };

  const inputNumberOnChange = (value: number): void => {
    setBuyInValue(value);
    // to move slider:
    form.setFieldsValue({
      baseAmount: value,
    });
  };

  const getGameTypeLabel = (): string => {
    if (!gameMode) return;
    if (gameMode === 'REGULAR' && isAllIn) return t(`common:all_in_game`);
    if (gameMode === 'OMAHA' && isAllIn) return t('common:all_in_game_omaha');
    if (gameMode) return t(`common:${gameMode.toString().toLocaleLowerCase()}`);
  };

  const getGameTypeImage = (): string => {
    if (!gameMode) return '/images-mobile/home/phaseTwo/open_table.webp';
    if (gameMode === 'REGULAR' && !isAllIn) return '/images-mobile/home/phaseTwo/long_card.webp';
    if (gameMode === 'REGULAR' && isAllIn) return '/images-mobile/home/phaseTwo/all_in.webp';
    if (gameMode === 'OMAHA' && isAllIn) return '/images-mobile/home/phaseTwo/all_in_omaha.webp';
    if (gameMode === 'OMAHA' && !isAllIn) return '/images-mobile/home/phaseTwo/omaha.webp';
    if (gameMode === 'SHORT') return '/images-mobile/home/phaseTwo/short_card.webp';
    return '/images-mobile/home/phaseTwo/open_table.webp';
  };

  return (
    <PopupContainer
      visible={visible}
      onCancel={async () => {
        if (onCancelCallback) {
          onCancelCallback();
        }
      }}
      mobileModalProps={{disableDrag: true, snapPoints: [500], disableScrollLocking: true}}
      desktopModalProps={{bodyStyle: {height: 500}}}
    >
      {/* // TODO  */}

      <div className={classes.container}>
        <img className={classes.buyInImage} src={getGameTypeImage()} alt="buyInImage" />
        <div className={classes.titleArea}>
          <div className={classes.modalTitle}>{t('buyin_option')}</div>
        </div>
        <div className={classes.modalContent}>
          {/* <div className={classes.buyInText}>
            {getGameTypeLabel()} ${smallBlind} / ${bigBlind}
          </div> */}
          <div className={classes.buyInLabel}>{t('buy_in_amount')}</div>
          <div className={classes.addOrMinusRow}>
            <div className={classes.inputArea}>
              <div className={classes.inputLabel}>USD$ </div>
              {/* <InputNumber
                className={classes.antInputNumber}
                // prefix="USD$"
                value={buyInValue}
                onChange={inputNumberOnChange}
                min={buyInMinAmount}
                max={buyInMaxAmount}
                disabled={!isBalanceEnough}
                step={stepUnit}
              /> */}
              <InputNumber
                className={classes.antInputNumber}
                // prefix="USD$"
                value={buyInValue}
                onChange={inputNumberOnChange}
                min={buyInMinAmount}
                max={buyInMaxAmount}
                disabled={!isBalanceEnough}
                controls={false}
                // step={stepUnit}
              />
            </div>
          </div>
          <div className={classes.remainingArea}>
            <span
              className={classNames(
                classes.remainingSpan,
                !isBalanceEnough && classes.remainingSpanLowBalance
              )}
            >
              {t('your_balance', {val: balance})}
            </span>
            <span className={classes.remainingSpanLowBalance}>
              {isBalanceEnough ? null : t('no_enough_balance', {val: buyInMinAmount})}
            </span>
          </div>
          <section className={classNames(classes.sliderRow, !isBalanceEnough && classes.hidden)}>
            <div className={classes.minMaxBuyInDiv}>
              <GradientButton
                onClick={() => {
                  form.setFieldsValue({
                    baseAmount: Number(buyInMinAmount),
                  });
                  setBuyInValue(Number(buyInMinAmount));
                }}
                size="small"
                // className={classes.addOrMinusBotton}

                style={{height: 40, minWidth: 60, borderRadius: 5}}
                disabled={!isBalanceEnough}
              >
                {t('action_buttons:min')}
              </GradientButton>
            </div>
            <div
              className={classNames(classes.sliderContainer, {
                [classes.hidden]: !isBalanceEnough,
              })}
            >
              <Form
                className={classes.formContainer}
                form={form}
                onFinish={async valueInForm => {
                  console.log('valueInForm', valueInForm);
                  onFinishCallback({base: valueInForm[FIELDS.BASE_AMOUNT]});
                }}
              >
                <Form.Item
                  name={FIELDS.BASE_AMOUNT}
                  initialValue={smallBlind}
                  rules={[{type: 'number', min: 0}]}
                >
                  <Slider
                    id="buyInValueSlider"
                    tooltip={{open: false}}
                    className={classNames(
                      sameMinMaxTriggered && classes.sameMinMaxTriggered,
                      classes.yellowSlider
                    )}
                    min={buyInMinAmount}
                    max={buyInMaxAmount}
                    onChange={debounceFunction(handleSliderOnChange, 5)}
                    disabled={false}
                    step={smallBlind}
                    handleStyle={{
                      borderRadius: 30,
                      transform: 'translate(-50%, -20%)',
                      border: 'none',
                      height: 30,
                      width: 30,
                      boxShadow:
                        '-5px -5px 10px 0px rgba(255, 255, 255, 0.05), 5px 5px 10px 0px rgba(0, 0, 0, 0.25), 1px 1px 1px 0px rgba(255, 255, 255, 0.10) inset',
                    }}
                    // marks API cant be used coz buyInMaxAmount can be 0 / NaN
                  />
                </Form.Item>
              </Form>
            </div>
            <div className={classes.minMaxBuyInDiv}>
              <GradientButton
                onClick={() => {
                  form.setFieldsValue({
                    baseAmount: Number(buyInMaxAmount),
                  });
                  setBuyInValue(Number(buyInMaxAmount));
                }}
                size="small"
                // className={classes.addOrMinusBotton}
                style={{height: 40, minWidth: 60, borderRadius: 5}}
                disabled={!isBalanceEnough}
              >
                {t('action_buttons:max')}
              </GradientButton>
            </div>
          </section>
          <div className={classNames(classes.minMaxSpanRow, !isBalanceEnough && classes.hidden)}>
            <span>$ {buyInMinAmount}</span>
            <span>$ {buyInMaxAmount}</span>
          </div>
        </div>
        <div className={classes.buyInButtonArea}>
          <GradientButton
            id="buyInButton"
            onClick={form.submit}
            disabled={!isBalanceEnough}
            loading={isBuyInLoading}
            style={{height: 50, fontSize: 16}}
          >
            <span className={classes.label}>{t('buy_in')}</span>
            {isBalanceEnough && dayjs(expiredAt).isAfter(dayjs()) ? (
              <>
                <span className={classes.buyInCountdown}>
                  (
                  <Countdown
                    valueStyle={{
                      color: 'white',
                      fontSize: 16,
                      display: 'inline',
                      // textShadow: '0px 0px 2px #241503, 0 0 2px #241503, 0 0 2px #241503',
                    }}
                    value={expiredAt}
                    format="s"
                  />
                  {`${t('seconds')}`})
                </span>
              </>
            ) : null}
          </GradientButton>
        </div>
      </div>
    </PopupContainer>
  );
};

export default React.memo(BuyInModal);
